import { z } from "zod";

import { INTAKE_FILED_IN, INTAKE_FOLDERS, INTAKE_QUERY_MAX_LENGTH } from "./intake-folders";
import type { Notification } from "./notifications";
import { listOfFields } from "./parties";

// Intake: the documents members upload for Document Filing to read, and the shapes of the requests that upload and
// list them. The API and the frontend share these.

/**
 * What Intake takes: PDFs, Word documents and pictures, judged by the file name's extension. A dragged `File` often
 * arrives with an empty MIME type, so the extension is the rule on both sides.
 */
export const INTAKE_EXTENSIONS = ["pdf", "docx", "png", "jpg", "jpeg"] as const;
export type IntakeExtension = (typeof INTAKE_EXTENSIONS)[number];

/** The largest file Intake takes: 100 MB, as 100 × 1024 × 1024 bytes. */
export const INTAKE_MAX_FILE_SIZE = 100 * 1024 * 1024;

/**
 * The most an organization's Intake documents may take together: 500 MB, as 500 × 1000 × 1000 bytes — the decimal units
 * the app shows sizes in (`formatFileSize`), so what it shows of an organization's storage adds up to the limit it
 * names. An organization holds each content once, so each document counts once, at the size it was uploaded with;
 * Ravnar's own copies of a document — what Bedrock read from it — do not count. The upload route refuses a file that
 * would take the organization past it, and the copier holds the line when uploads race for the last of it
 * (`apps/backend/lib/api/intake/upload/copier.ts`).
 */
export const INTAKE_STORAGE_LIMIT = 500_000_000;

/** The storage limit as the app names it: "500 MB". */
export const INTAKE_STORAGE_LIMIT_LABEL = `${String(INTAKE_STORAGE_LIMIT / 1_000_000)} MB`;

// Two of Amazon Bedrock Data Automation's own limits, which are what Intake can take rather than what it would like to.
// The size limit above is Intake's own number and sits well inside Bedrock's 500 MB; these two are Bedrock's, and the
// drop zone turns a file away on them before anybody waits for a document that could never be read
// (`apps/frontend/src/lib/intake-limits.ts`). See `docs/intake.md`.

/** The largest a picture may be on either side for Bedrock to read it as a document: 10,000 pixels. */
export const BDA_MAX_IMAGE_PIXELS = 10_000;

/**
 * The most pages Bedrock reads out of one document. It is 20 without the document splitter and 3,000 with it, and
 * `IntakeStack` turns the splitter on (`apps/backend/lib/stacks/intake-stack.ts`); change one and the other is wrong.
 */
export const BDA_MAX_DOCUMENT_PAGES = 3000;

/** The extension of a file Intake takes, from its name, in lowercase: "Invoice.PDF" → "pdf"; undefined for any other file. */
export function intakeExtensionOf(fileName: string): IntakeExtension | undefined {
	const dot = fileName.lastIndexOf(".");
	const extension = dot === -1 ? "" : fileName.slice(dot + 1).toLowerCase();
	return INTAKE_EXTENSIONS.find(accepted => accepted === extension);
}

/** `184320` → `"184 KB"`. Decimal units, as file managers show them. The page and the audit log say sizes this way. */
export function formatFileSize(bytes: number): string {
	if (bytes < 1000) {
		return `${bytes} B`;
	}
	if (bytes < 1_000_000) {
		return `${Math.round(bytes / 1000)} KB`;
	}
	if (bytes < 1_000_000_000) {
		return `${(bytes / 1_000_000).toFixed(1)} MB`;
	}
	return `${(bytes / 1_000_000_000).toFixed(1)} GB`;
}

/** A file name as the API keeps it: without surrounding spaces, and with an extension Intake takes. */
export const intakeFileNameSchema = z
	.string()
	.trim()
	.min(1, "File name is required")
	.max(255, "Use at most 255 characters")
	.refine(fileName => intakeExtensionOf(fileName) !== undefined, "Intake takes PDF, Word and image files");

/**
 * Where a file stands: `pending` until its upload lands in the upload bucket, `stored` once the copier has put its
 * content in the storage bucket, `processing` while Bedrock and then the Directory agent read it and `processed` once
 * they have, `rejected` when the upload was not the size that was announced. A document's run sets `processing` at its
 * "Hand to Bedrock" step, when Bedrock Data Automation takes the document up
 * (`apps/backend/lib/api/intake/processing/start-processor.ts`), and `processed` at its "Mark processed" step, right
 * after the Directory agent and "Tell the uploader" (`apps/backend/lib/api/intake/agents/mark-processed.ts`); the
 * Document linker and Document Filing read the document after that, in the same run, and leave its status as it is.
 * Both are statuses the Intake page lists beside `stored`, and the demo organization seeds one of each.
 */
export const INTAKE_FILE_STATUSES = ["pending", "stored", "processing", "processed", "rejected"] as const;
export type IntakeFileStatus = (typeof INTAKE_FILE_STATUSES)[number];

/**
 * The statuses a file has once it is a document: the Intake list answers these unless a request names one status. A
 * file still on its way (`pending`) and one the copier refused for its size (`rejected`) are not documents; the rail
 * badge asks for `pending` by name.
 */
export const INTAKE_DOCUMENT_STATUSES = [
	"stored",
	"processing",
	"processed",
] as const satisfies readonly IntakeFileStatus[];
export type IntakeDocumentStatus = (typeof INTAKE_DOCUMENT_STATUSES)[number];

/**
 * Who uploaded a file: the member's id (their Cognito `sub`) and their name at the time — or, for a file software
 * uploaded through the integration API, the API key's id and the key as the audit log names it, "API key HubSpot sync
 * (rvn_…Qm3x)" (`apiKeyUploader` in `./integration.ts`), marked `apiKey`. Software has no bell, so nobody is notified
 * of what becomes of a file a key uploaded: the software asks the integration API instead.
 */
export const intakeUploaderSchema = z.object({
	id: z.string().min(1),
	name: z.string(),
	/** True for a file software uploaded with one of the organization's API keys; absent for a member's. */
	apiKey: z.literal(true).optional(),
});
export type IntakeUploader = z.infer<typeof intakeUploaderSchema>;

/** The SHA-256 of a file's content, as 64 lowercase hex digits. */
export const sha256Schema = z.string().regex(/^[\da-f]{64}$/, "Invalid SHA-256");

export const intakeFileSchema = z.object({
	id: z.string().min(1),
	/** The file name as it was uploaded. */
	name: z.string().min(1),
	/** Size in bytes, as announced when the upload was requested; the upload must be exactly this long. */
	size: z.int().positive().max(INTAKE_MAX_FILE_SIZE),
	/** The content type the file is stored with, chosen from its extension. */
	contentType: z.string().min(1),
	status: z.enum(INTAKE_FILE_STATUSES),
	/** When the upload was requested, as an ISO 8601 timestamp in UTC. */
	uploadedAt: z.iso.datetime(),
	uploadedBy: intakeUploaderSchema,
	/** The SHA-256 of the content, once stored. An organization stores each hash once, under one file. */
	sha256: sha256Schema.optional(),
	/** When the content was stored, once it is. */
	storedAt: z.iso.datetime().optional(),
});
export type IntakeFile = z.infer<typeof intakeFileSchema>;

/**
 * A file that is a document, in one of `INTAKE_DOCUMENT_STATUSES`: what `GET /api/intake/files/{fileId}` answers with,
 * and what a document's own page shows. A file still on its way, or one the copier refused, is not one.
 */
export const intakeDocumentSchema = intakeFileSchema.extend({ status: z.enum(INTAKE_DOCUMENT_STATUSES) });
export type IntakeDocument = z.infer<typeof intakeDocumentSchema>;

/** Whether a file is a document: stored, and being read or already read. The API and the fake API answer only these. */
export function isIntakeDocument(file: IntakeFile): file is IntakeDocument {
	const documentStatuses: readonly IntakeFileStatus[] = INTAKE_DOCUMENT_STATUSES;
	return documentStatuses.includes(file.status);
}

/** What Intake sends to upload a file; the API answers with where to put it. */
export const createIntakeUploadRequestSchema = z.object({
	fileName: intakeFileNameSchema,
	size: z
		.int("Size must be a whole number of bytes")
		.positive("The file is empty")
		.max(INTAKE_MAX_FILE_SIZE, "Files can be at most 100 MB"),
});
export type CreateIntakeUploadRequest = z.infer<typeof createIntakeUploadRequestSchema>;

/**
 * The file as the API saved it, still pending, and where to put its content: a presigned URL to PUT exactly `size`
 * bytes to, as the request body and nothing else, before `expiresAt`. In AWS the URL is absolute and names the upload
 * bucket; the local server answers a path on the app's own origin (`/api/local-uploads/…`), which the Vite proxy
 * forwards to it, so the browser resolves it against the page.
 */
export const createIntakeUploadResponseSchema = z.object({
	file: intakeFileSchema,
	upload: z.object({ url: z.string().min(1), method: z.literal("PUT"), expiresAt: z.iso.datetime() }),
});
export type CreateIntakeUploadResponse = z.infer<typeof createIntakeUploadResponseSchema>;

/**
 * Why the upload route turns a file away when it would take the organization past `INTAKE_STORAGE_LIMIT`, `used` being
 * what its documents take already: "invoice.pdf does not fit: the organization has 1.2 MB of its 500 MB of storage
 * left." — or, once nothing is left, "invoice.pdf does not fit: the organization has used all 500 MB of its storage."
 * The route answers it with a 409, and the frontend's fake API says the same.
 */
export function storageFullMessage(fileName: string, used: number): string {
	const left = INTAKE_STORAGE_LIMIT - used;
	const has =
		left > 0
			? `has ${formatFileSize(left)} of its ${INTAKE_STORAGE_LIMIT_LABEL} of storage left`
			: `has used all ${INTAKE_STORAGE_LIMIT_LABEL} of its storage`;
	return `${fileName} does not fit: the organization ${has}.`;
}

/** The Intake list comes a page of this many documents at a time, newest first. */
export const INTAKE_PAGE_SIZE = 10;

/** A link to another page of the Intake list. The API makes it; a client passes it back unchanged and never reads it. */
export const intakePageTokenSchema = z.string().regex(/^[\w-]{1,512}$/, "Invalid page");

/**
 * The query of `GET /api/intake/files`: one status to show (the document statuses when there is none), whether to show
 * only what the asking member uploaded, the folder to show the files of, and below it (`./intake-folders.ts`; every
 * file when there is none), what the search box holds (`intakeDocumentHolds`), and the page to show (the newest when
 * there is none). `mine` arrives as the string a URL carries.
 */
export const intakeFilesQuerySchema = z.object({
	status: z.enum(INTAKE_FILE_STATUSES).optional(),
	mine: z.literal("true").optional(),
	folder: z.enum(INTAKE_FOLDERS).optional(),
	q: z.string().max(INTAKE_QUERY_MAX_LENGTH).optional(),
	page: intakePageTokenSchema.optional(),
});
export type IntakeFilesQuery = z.infer<typeof intakeFilesQuerySchema>;

/**
 * A file as the Intake list shows it: the file, the folder it is in (`intakeFiledIn`), and who it is with and its
 * number, when Document Filing read them.
 */
export const intakeListedFileSchema = intakeFileSchema.extend({
	filedIn: z.enum(INTAKE_FILED_IN),
	counterparty: z.string().optional(),
	number: z.string().optional(),
});
export type IntakeListedFile = z.infer<typeof intakeListedFileSchema>;

/** A folder inside the one listed, and how many of the files the query keeps it holds. */
export const intakeFolderCountSchema = z.object({
	folder: z.enum(INTAKE_FOLDERS),
	count: z.int().nonnegative(),
});
export type IntakeFolderCount = z.infer<typeof intakeFolderCountSchema>;

/**
 * One page of the Intake list, newest first, with links to the pages beside it (`null` where there is none); every
 * folder inside the one listed, in the catalogue's order (`intakeFoldersIn`), each with how many of the files the query
 * keeps it holds; and how many the folder listed holds, on every page.
 */
export const intakeFilesPageSchema = z.object({
	files: z.array(intakeListedFileSchema).max(INTAKE_PAGE_SIZE),
	folders: z.array(intakeFolderCountSchema),
	total: z.int().nonnegative(),
	/** Older documents. */
	next: intakePageTokenSchema.nullable(),
	/** Newer documents. */
	previous: intakePageTokenSchema.nullable(),
});
export type IntakeFilesPage = z.infer<typeof intakeFilesPageSchema>;

/**
 * Where to get a document: a presigned URL to GET, good until `expiresAt`. The browser follows it as a navigation, and
 * the response carries the document as an attachment under the name it was uploaded with.
 */
export const intakeDownloadResponseSchema = z.object({
	url: z.string().min(1),
	expiresAt: z.iso.datetime(),
});
export type IntakeDownloadResponse = z.infer<typeof intakeDownloadResponseSchema>;

/**
 * Where to show a document from: a presigned URL to GET, good until `expiresAt`. Its response carries the document
 * inline, under the name it was uploaded with, so a browser shows it instead of saving it: a document's page puts a PDF
 * in a frame and a picture in an image, and reads a Word document's bytes for its renderer. It lives longer than a
 * download's, because a browser reads a long PDF a piece at a time.
 */
export const intakePreviewResponseSchema = z.object({
	url: z.string().min(1),
	expiresAt: z.iso.datetime(),
});
export type IntakePreviewResponse = z.infer<typeof intakePreviewResponseSchema>;

/** `text` whole when it fits in `limit` characters, and otherwise cut to fit with a single-character ellipsis. */
function clipped(text: string, limit: number): string {
	return text.length <= limit ? text : `${text.slice(0, limit - 1).trimEnd()}…`;
}

/**
 * How much of the upload's name the title of an upload that was not added carries — a duplicate's, or one there was no
 * room for: " was not added to Intake" takes the other 24 of its 160.
 */
const NOT_ADDED_FILE_NAME_CHARACTERS = 136;

/** How much of the name the organization holds the document under, and of who uploaded it, a duplicate's description carries. */
const HOLDER_FILE_NAME_CHARACTERS = 200;
const HOLDER_UPLOADER_NAME_CHARACTERS = 60;

/**
 * What the copier tells whoever uploaded a document the organization already holds as `existing`, a stored file:
 * "receipt.jpg was not added to Intake" / "It is already there: Mara B. uploaded it as receipt-taxi.jpg." The
 * frontend's fake API says the same. `uploaderId` is who uploaded, who is told "you" when the existing file is their
 * own. A long name is shortened, so however long the names are it fits a notification's 160-character title and
 * 320-character description (`./notifications.ts`): a file name may be 255 characters long.
 */
export function duplicateUploadNotification(
	fileName: string,
	existing: IntakeFile,
	uploaderId: string,
): Pick<Notification, "title" | "description"> {
	const who =
		existing.uploadedBy.id === uploaderId
			? "you"
			: clipped(existing.uploadedBy.name, HOLDER_UPLOADER_NAME_CHARACTERS);
	const as = existing.name === fileName ? "" : ` as ${clipped(existing.name, HOLDER_FILE_NAME_CHARACTERS)}`;
	return {
		title: `${clipped(fileName, NOT_ADDED_FILE_NAME_CHARACTERS)} was not added to Intake`,
		description: `It is already there: ${who} uploaded it${as}.`,
	};
}

/**
 * What the copier tells whoever uploaded a document there was no room for, when other uploads took what was left of
 * the organization's storage while this one was on its way: "scan.pdf was not added to Intake" / "It does not fit in
 * what is left of the organization's 500 MB of storage." The frontend's fake API says the same. A long name is
 * shortened, as a duplicate's is, so it fits a notification's 160-character title.
 */
export function storageFullNotification(fileName: string): Pick<Notification, "title" | "description"> {
	return {
		title: `${clipped(fileName, NOT_ADDED_FILE_NAME_CHARACTERS)} was not added to Intake`,
		description: `It does not fit in what is left of the organization's ${INTAKE_STORAGE_LIMIT_LABEL} of storage.`,
	};
}

/** How much of a file's name a notification about the parties found in it carries. */
const NOTIFIED_FILE_NAME_CHARACTERS = 80;

/** How much of a party's name that notification carries. */
const NOTIFIED_PARTY_NAME_CHARACTERS = 50;

/** How many parties that notification names before it counts the rest. */
const NOTIFIED_PARTIES = 3;

/**
 * What whoever uploaded a document is told once the Directory agent has added parties from it: "Zelena Taxi from
 * receipt.jpg needs details" / "The Directory agent added Zelena Taxi from receipt.jpg. Fill in what it lacks." — or,
 * for several, "2 parties from invoice.pdf need details" / "The Directory agent added Zelena Taxi and Acme d.o.o. from
 * invoice.pdf. Fill in what each of them lacks." `names` are the parties it added, in the order it added them, and
 * never none. It names three at most and counts the rest, and shortens a long name, so however long the names are it
 * fits a notification's 160-character title and 320-character description (`./notifications.ts`).
 */
export function partiesAddedNotification(
	fileName: string,
	names: readonly string[],
): Pick<Notification, "title" | "description"> {
	const file = clipped(fileName, NOTIFIED_FILE_NAME_CHARACTERS);
	const [only] = names;
	const title =
		only !== undefined && names.length === 1
			? `${clipped(only, NOTIFIED_PARTY_NAME_CHARACTERS)} from ${file} needs details`
			: `${String(names.length)} parties from ${file} need details`;
	const named = names.slice(0, NOTIFIED_PARTIES).map(name => clipped(name, NOTIFIED_PARTY_NAME_CHARACTERS));
	const rest = names.length - named.length;
	const who = listOfFields(rest > 0 ? [...named, `${String(rest)} more`] : named);
	const lacks = names.length === 1 ? "it lacks" : "each of them lacks";
	return { title, description: `The Directory agent added ${who} from ${file}. Fill in what ${lacks}.` };
}

/**
 * What one of Intake's audit entries says, beyond what every entry carries. The copier records it
 * (`apps/backend/lib/api/intake/upload/copier.ts`) and the frontend's fake API says the same.
 */
export interface IntakeAuditEvent {
	/** What happened, in a few words. */
	readonly action: string;
	/** Which document, and what about it. */
	readonly detail: string;
}

/** The audit entry for a document that was added to Intake: "Document added" / "invoice.pdf · 184 KB". */
export function documentAddedAudit(file: IntakeFile): IntakeAuditEvent {
	return { action: "Document added", detail: `${file.name} · ${formatFileSize(file.size)}` };
}

/** The audit entry for a document someone downloaded: "Document downloaded" / "invoice.pdf · 184 KB". */
export function documentDownloadedAudit(file: IntakeFile): IntakeAuditEvent {
	return { action: "Document downloaded", detail: `${file.name} · ${formatFileSize(file.size)}` };
}

/**
 * The audit entry for a document someone opened on its own page: "Document viewed" / "invoice.pdf · 184 KB". Showing a
 * document hands its content to the browser as surely as a download does.
 */
export function documentViewedAudit(file: IntakeFile): IntakeAuditEvent {
	return { action: "Document viewed", detail: `${file.name} · ${formatFileSize(file.size)}` };
}

/**
 * The audit entry for an upload whose content the organization already holds as `existing`, which is never stored:
 * "Duplicate document turned away" / "receipt.jpg · already in Intake as receipt-taxi.jpg".
 */
export function duplicateDocumentAudit(fileName: string, existing: IntakeFile): IntakeAuditEvent {
	const as = existing.name === fileName ? "" : ` as ${existing.name}`;
	return { action: "Duplicate document turned away", detail: `${fileName} · already in Intake${as}` };
}

/**
 * The audit entry for an upload that was not the size its file announced, which is never stored either: "Upload
 * rejected" / "scan.png · 2.4 MB instead of the announced 1.2 MB".
 */
export function rejectedUploadAudit(file: IntakeFile, uploadedSize: number): IntakeAuditEvent {
	return {
		action: "Upload rejected",
		detail: `${file.name} · ${formatFileSize(uploadedSize)} instead of the announced ${formatFileSize(file.size)}`,
	};
}

/**
 * The audit entry for an upload there was no room for, which is never stored either: "Upload turned away" / "scan.pdf ·
 * 12.4 MB · more than is left of the organization's 500 MB of storage".
 */
export function storageFullAudit(file: IntakeFile): IntakeAuditEvent {
	const room = `more than is left of the organization's ${INTAKE_STORAGE_LIMIT_LABEL} of storage`;
	return { action: "Upload turned away", detail: `${file.name} · ${formatFileSize(file.size)} · ${room}` };
}
